import { OwnershipType, PersonaType, WorkPackageOwnerFilterByTerm } from '@bechtel/types';
import {
  AllForemanId,
  AllGeneralForemanId,
  AllSuperintendentId,
  UnassignedForemanId,
  UnassignedGeneralForemanId,
  UnassignedSuperintendentId,
} from '../planning/form-data';

export interface FilterOwnersInput {
  selectedSuperintendent?: { id: string } | null;
  selectedGeneralForeman?: { id: string } | null;
  selectedForeman?: { id: string } | null;
  filterByUnassignedPersona?: PersonaType[];
}

/**
 * Builds work package owner filter based on selected superintendents, general foremen, and foremen.
 * This is used for filtering work packages in both planning list and update schedule operations.
 *
 * @param input - Object containing selected crew hierarchy and unassigned persona filters
 * @returns Array of WorkPackageOwnerFilterByTerm objects representing the filter criteria
 */
export function buildWorkPackageOwnerFilter(input: FilterOwnersInput): WorkPackageOwnerFilterByTerm[] {
  const { selectedSuperintendent, selectedGeneralForeman, selectedForeman, filterByUnassignedPersona = [] } = input;

  const filterByOwners: WorkPackageOwnerFilterByTerm[] = [];

  // Add field engineer unassigned from filter input
  if (filterByUnassignedPersona.includes(PersonaType.FIELD_ENGINEER)) {
    filterByOwners.push({ persona: PersonaType.FIELD_ENGINEER, unassigned: true });
  }

  // If "All Superintendents" is selected, return only FE unassigned (if any)
  if (selectedSuperintendent?.id === AllSuperintendentId) {
    return filterByOwners;
  }

  // Handle "Unassigned Superintendent"
  if (selectedSuperintendent?.id === UnassignedSuperintendentId) {
    filterByOwners.push({ persona: PersonaType.SUPERINTENDENT, unassigned: true });
    return filterByOwners;
  }

  // Handle "All General Foremen" for a specific Superintendent
  if (selectedGeneralForeman?.id === AllGeneralForemanId && selectedSuperintendent?.id) {
    filterByOwners.push({ persona: PersonaType.SUPERINTENDENT, crewMemberId: selectedSuperintendent.id });
    return filterByOwners;
  }

  // Handle "Unassigned General Foreman"
  if (selectedGeneralForeman?.id === UnassignedGeneralForemanId && selectedSuperintendent?.id) {
    filterByOwners.push({ persona: PersonaType.SUPERINTENDENT, crewMemberId: selectedSuperintendent.id });
    filterByOwners.push({ persona: PersonaType.GENERAL_FOREMAN, unassigned: true });
    return filterByOwners;
  }

  // Handle "All Foremen" for a specific GF and Superintendent
  if (selectedForeman?.id === AllForemanId && selectedSuperintendent?.id && selectedGeneralForeman?.id) {
    filterByOwners.push({ persona: PersonaType.SUPERINTENDENT, crewMemberId: selectedSuperintendent.id });
    filterByOwners.push({ persona: PersonaType.GENERAL_FOREMAN, crewMemberId: selectedGeneralForeman.id });
    return filterByOwners;
  }

  // Handle "Unassigned Foreman"
  if (selectedForeman?.id === UnassignedForemanId && selectedSuperintendent?.id && selectedGeneralForeman?.id) {
    filterByOwners.push({ persona: PersonaType.SUPERINTENDENT, crewMemberId: selectedSuperintendent.id });
    filterByOwners.push({ persona: PersonaType.GENERAL_FOREMAN, crewMemberId: selectedGeneralForeman.id });
    filterByOwners.push({ persona: PersonaType.FOREMAN, unassigned: true });
    return filterByOwners;
  }

  // All specific personas are selected - add primary ownership for all
  if (selectedSuperintendent?.id && selectedGeneralForeman?.id && selectedForeman?.id) {
    filterByOwners.push({
      persona: PersonaType.SUPERINTENDENT,
      crewMemberId: selectedSuperintendent.id,
      ownershipType: OwnershipType.PRIMARY,
    });
    filterByOwners.push({
      persona: PersonaType.GENERAL_FOREMAN,
      crewMemberId: selectedGeneralForeman.id,
      ownershipType: OwnershipType.PRIMARY,
    });
    filterByOwners.push({
      persona: PersonaType.FOREMAN,
      crewMemberId: selectedForeman.id,
      ownershipType: OwnershipType.PRIMARY,
    });
  }

  return filterByOwners;
}
